import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import { useRecoilValue } from "recoil";
import { scopePreferenceState, activeScopeState } from "../global-state";

// Helper function to format date based on user preference or default
const formatDateWithPreference = (date, dateFormat = null) => {
  // Ensure we have a dayjs object
  const dayjsDate = typeof date === "string" ? dayjs(date) : date;

  if (!dayjsDate || !dayjsDate.isValid()) {
    return date; // Return original if we can't parse it
  }

  if (!dateFormat) {
    // Default format: YYYY/MMM/DD with uppercase month
    const year = dayjsDate.format("YYYY");
    const month = dayjsDate.format("MMM").toUpperCase();
    const day = dayjsDate.format("DD");
    return `${year}/${month}/${day}`;
  }

  // Use user preferred format
  switch (dateFormat) {
    case "yyyy-MM-dd":
      return dayjsDate.format("YYYY-MM-DD");
    case "MM/dd/yyyy":
      return dayjsDate.format("MM/DD/YYYY");
    case "dd/MM/yyyy":
      return dayjsDate.format("DD/MM/YYYY");
    default:
      // Fallback to default format
      const year = dayjsDate.format("YYYY");
      const month = dayjsDate.format("MMM").toUpperCase();
      const day = dayjsDate.format("DD");
      return `${year}/${month}/${day}`;
  }
};

// Helper function to format date as YYYY/MMM/DD with uppercase month (legacy)
const formatDateToUppercase = (date) => {
  const year = date.format("YYYY");
  const month = date.format("MMM").toUpperCase();
  const day = date.format("DD");
  return `${year}/${month}/${day}`;
};
import {
  Select,
  MenuItem,
  FormControl,
  Typography,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
} from "@mhome/ui";
import { StaticDatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import useMediaQuery from "@mui/material/useMediaQuery";
import { safeAreaInsetsState } from "../global-state";

const WEEKDAYS = [
  { value: "DAILY", label: "Everyday" },
  { value: "SUNDAY", label: "Sunday" },
  { value: "MONDAY", label: "Monday" },
  { value: "TUESDAY", label: "Tuesday" },
  { value: "WEDNESDAY", label: "Wednesday" },
  { value: "THURSDAY", label: "Thursday" },
  { value: "FRIDAY", label: "Friday" },
  { value: "SATURDAY", label: "Saturday" },
];

const RELATIVE_DATES = [
  { value: "TODAY", label: "Today" },
  { value: "TOMORROW", label: "Tomorrow" },
  { value: "DAY_AFTER_TOMORROW", label: "Two days from today" },
  { value: "THREE_DAYS_FROM_TODAY", label: "Three days from today" },
  { value: "FOUR_DAYS_FROM_TODAY", label: "Four days from today" },
];

const MONTHS = [
  { value: "MONTHLY", label: "Every month" },
  { value: "JAN", label: "January" },
  { value: "FEB", label: "February" },
  { value: "MAR", label: "March" },
  { value: "APR", label: "April" },
  { value: "MAY", label: "May" },
  { value: "JUN", label: "June" },
  { value: "JUL", label: "July" },
  { value: "AUG", label: "August" },
  { value: "SEP", label: "September" },
  { value: "OCT", label: "October" },
  { value: "NOV", label: "November" },
  { value: "DEC", label: "December" },
];

const WEEKDAY_OPTIONS = [
  { value: "MONDAY", label: "Monday" },
  { value: "TUESDAY", label: "Tuesday" },
  { value: "WEDNESDAY", label: "Wednesday" },
  { value: "THURSDAY", label: "Thursday" },
  { value: "FRIDAY", label: "Friday" },
  { value: "SATURDAY", label: "Saturday" },
  { value: "SUNDAY", label: "Sunday" },
];

const generateYearOptions = () => {
  const currentYear = new Date().getFullYear();
  const years = [{ value: "YEARLY", label: "Every year" }];
  for (let year = currentYear; year <= 2050; year++) {
    years.push({ value: year.toString(), label: year.toString() });
  }
  return years;
};

const generateDayOptions = () => {
  const days = [...WEEKDAY_OPTIONS];
  for (let day = 1; day <= 31; day++) {
    const dayStr = day.toString().padStart(2, "0");
    days.push({ value: dayStr, label: dayStr });
  }
  return days;
};

const DateGeneralInput = ({ valueSpec, onChange, zIndex = 100 }) => {
  const [tabValue, setTabValue] = useState(0); // 0=regular, 1=weekday, 2=relative, 3=custom
  const [regularDate, setRegularDate] = useState(
    formatDateToUppercase(dayjs())
  );
  const [regularDateValue, setRegularDateValue] = useState(null);
  const [weekdayDate, setWeekdayDate] = useState("MONDAY");
  const [relativeDate, setRelativeDate] = useState("TODAY");
  const [customYear, setCustomYear] = useState("YEARLY");
  const [customMonth, setCustomMonth] = useState("MONTHLY");
  const [customDay, setCustomDay] = useState("01");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [isInvalidValue, setIsInvalidValue] = useState(false);
  const [displayValue, setDisplayValue] = useState("");
  const isSmallScreen = useMediaQuery("@media (max-width:959px)");
  const safeAreaInsets = useRecoilValue(safeAreaInsetsState);
  const isReadOnly = valueSpec.readOnly;
  const scopePreference = useRecoilValue(scopePreferenceState);
  const activeScope = useRecoilValue(activeScopeState);
  const userDateFormat = scopePreference?.[activeScope]?.dateFormat;

  // Helper function to generate descriptive text for custom dates
  const getCustomDateDisplayText = (year, month, day) => {
    const yearText = year === "YEARLY" ? "Every year" : year;
    const monthText =
      month === "MONTHLY"
        ? "Every month"
        : MONTHS.find((m) => m.value === month)?.label || month;

    // Check if day is a weekday or numeric
    const weekdayOption = WEEKDAY_OPTIONS.find((w) => w.value === day);
    const dayText = weekdayOption
      ? weekdayOption.label
      : day.endsWith("01")
      ? "1st"
      : day.endsWith("02")
      ? "2nd"
      : day.endsWith("03")
      ? "3rd"
      : day.endsWith("21")
      ? "21st"
      : day.endsWith("22")
      ? "22nd"
      : day.endsWith("23")
      ? "23rd"
      : day.endsWith("31")
      ? "31st"
      : day + "th";

    return `${yearText}, ${monthText}, ${dayText}`;
  };

  const yearOptions = generateYearOptions();
  const dayOptions = generateDayOptions();

  // Parse the current value to determine format and set initial state (only on first load)
  useEffect(() => {
    if (initialized) return; // Only run once during initialization

    if (!valueSpec.value) {
      setIsInvalidValue(true);
      setDisplayValue("unknown date");
      setInitialized(true);
      return;
    }

    const value = valueSpec.value;
    let hasValidFormat = false;

    // Check for weekday format (MONDAY, TUESDAY, etc.)
    if (WEEKDAYS.some((wd) => wd.value === value)) {
      setTabValue(1);
      setWeekdayDate(value);
      hasValidFormat = true;
    }
    // Check for relative date format
    else if (RELATIVE_DATES.some((rd) => rd.value === value)) {
      setTabValue(2);
      setRelativeDate(value);
      hasValidFormat = true;
    }
    // Check for regular date format (YYYY/MMM/DD, YYYY-MM-DD, etc.)
    else {
      const regularDateParsed = dayjs(value);
      if (
        regularDateParsed.isValid() &&
        (value.match(/^\d{4}\/[A-Z]{3}\/\d{2}$/) ||
          value.match(/^\d{4}\/[a-z]{3}\/\d{2}$/i) ||
          value.match(/^\d{4}-\d{2}-\d{2}$/) ||
          value.match(/^\d{4}\/\d{2}\/\d{2}$/) ||
          value.match(/^\d{4}-\d{1,2}-\d{1,2}$/) ||
          value.match(/^\d{4}\/\d{1,2}\/\d{1,2}$/))
      ) {
        setTabValue(0);
        setRegularDate(formatDateToUppercase(regularDateParsed));
        setRegularDateValue(regularDateParsed);
        hasValidFormat = true;
      }
      // Check for custom format (2025/JAN/01, YEARLY/MONTHLY/FRIDAY, etc.)
      else {
        const customFormatRegex =
          /^(?:(\d{4}|YEARLY)\/)?([A-Z]+|MONTHLY)\/(.+)$/;
        const customMatch = value.match(customFormatRegex);
        if (customMatch) {
          setTabValue(3);
          setCustomYear(customMatch[1] || "YEARLY");
          setCustomMonth(customMatch[2]);
          setCustomDay(customMatch[3]);
          hasValidFormat = true;
        }
      }
    }

    if (hasValidFormat) {
      setIsInvalidValue(false);
      setDisplayValue("");
    } else {
      // Invalid format - just display the raw value with error styling
      setIsInvalidValue(true);
      setDisplayValue(value);
    }

    setInitialized(true);
  }, [valueSpec.value, initialized]);

  // Update regular date value when regularDate changes
  useEffect(() => {
    if (regularDate) {
      setRegularDateValue(dayjs(regularDate));
    }
  }, [regularDate]);

  // Note: Date format preference only affects display, not the stored value
  // The regularDate is always stored in YYYY/MMM/DD format internally

  const handleSectionClick = (sectionIndex) => {
    if (isReadOnly) return;
    setTabValue(sectionIndex);
  };

  const handleRegularDateChange = (data) => {
    if (isReadOnly) return;
    // Always store in consistent YYYY/MMM/DD format internally
    const date = formatDateToUppercase(data);
    setRegularDateValue(data);
    setRegularDate(date);
  };

  const handleWeekdayDateChange = (event) => {
    if (isReadOnly) return;
    const newWeekday = event.target.value;
    setWeekdayDate(newWeekday);
  };

  const handleRelativeDateChange = (event) => {
    if (isReadOnly) return;
    const newRelativeDate = event.target.value;
    setRelativeDate(newRelativeDate);
  };

  const handleCustomYearChange = (event) => {
    if (isReadOnly) return;
    const newYear = event.target.value;
    setCustomYear(newYear);
  };

  const handleCustomMonthChange = (event) => {
    if (isReadOnly) return;
    const newMonth = event.target.value;
    setCustomMonth(newMonth);
  };

  const handleCustomDayChange = (event) => {
    if (isReadOnly) return;
    const newDay = event.target.value;
    setCustomDay(newDay);
  };

  const getDisplayText = () => {
    if (isInvalidValue) {
      return displayValue;
    }

    if (tabValue === 0) {
      if (!regularDate) return "";
      return formatDateWithPreference(regularDate, userDateFormat);
    } else if (tabValue === 1) {
      if (!weekdayDate) return "";
      const weekdayLabel =
        WEEKDAYS.find((wd) => wd.value === weekdayDate)?.label || weekdayDate;
      return weekdayLabel;
    } else if (tabValue === 2) {
      if (!relativeDate) return "";
      const relativeLabel =
        RELATIVE_DATES.find((rd) => rd.value === relativeDate)?.label ||
        relativeDate;
      return relativeLabel;
    } else if (tabValue === 3) {
      if (!customMonth || !customDay) return "";
      return getCustomDateDisplayText(customYear, customMonth, customDay);
    }
    return "";
  };

  const handleDialogOpen = () => {
    if (!isReadOnly) {
      // Set default values for dialog if we don't have any values yet
      if (!regularDate && !weekdayDate && !relativeDate && !customMonth) {
        setTabValue(0);
        setRegularDate(formatDateToUppercase(dayjs()));
        setRegularDateValue(dayjs());
        setWeekdayDate("MONDAY");
        setRelativeDate("TODAY");
        setCustomYear("YEARLY");
        setCustomMonth("MONTHLY");
        setCustomDay("01");
      }
      setDialogOpen(true);
    }
  };

  const getCurrentValue = () => {
    if (tabValue === 0) {
      // Always return the stored format (YYYY/MMM/DD) regardless of display preference
      return regularDate || formatDateToUppercase(dayjs());
    } else if (tabValue === 1) {
      return weekdayDate;
    } else if (tabValue === 2) {
      return relativeDate;
    } else if (tabValue === 3) {
      let result = "";
      if (customYear !== "YEARLY") {
        result += `${customYear}/`;
      }
      result += `${customMonth}/${customDay}`;
      return result;
    }
    return "";
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleCancel = () => {
    setDialogOpen(false);
  };

  const handleSave = () => {
    const currentValue = getCurrentValue();
    console.log("currentValue", currentValue);
    // Clear invalid state since we now have a valid value
    setIsInvalidValue(false);
    setDisplayValue("");
    onChange(currentValue);
    setDialogOpen(false);
  };

  const renderContent = () => (
    <div
      className="flex flex-col w-full max-w-none max-h-none gap-0 overflow-auto"
      style={{
        height: isSmallScreen ? "calc(100vh - 64px)" : "100%",
      }}
    >
      {/* Regular Date Section */}
      <div
        onClick={() => handleSectionClick(0)}
        className="w-full relative p-4 transition-all duration-200"
        style={{
          borderTop: "1px solid",
          borderBottom: "1px solid",
          borderColor:
            tabValue === 0 ? "hsl(var(--primary))" : "hsl(var(--border))",
          zIndex: tabValue === 0 ? 2 : 1,
          cursor: isReadOnly ? "default" : "pointer",
          opacity: tabValue === 0 ? 1 : 0.4,
          backgroundColor:
            tabValue === 0 ? "hsl(var(--card))" : "rgba(0, 0, 0, 0.02)",
          pointerEvents: tabValue === 0 ? "auto" : isReadOnly ? "none" : "auto",
          WebkitTapHighlightColor: "transparent",
        }}
        onMouseEnter={(e) => {
          if (!isReadOnly) {
            e.currentTarget.style.borderColor =
              tabValue === 0 ? "hsl(var(--primary))" : "rgba(0, 0, 0, 0.3)";
            e.currentTarget.style.opacity = tabValue === 0 ? "1" : "0.6";
          }
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor =
            tabValue === 0 ? "hsl(var(--primary))" : "hsl(var(--border))";
          e.currentTarget.style.opacity = tabValue === 0 ? "1" : "0.4";
        }}
      >
        <Typography className="mb-1 font-bold text-sm">Date</Typography>
        {tabValue === 0 ? (
          <div className="min-h-[350px] flex flex-col items-stretch justify-center">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <StaticDatePicker
                value={regularDateValue}
                onChange={handleRegularDateChange}
                readOnly={isReadOnly}
                displayStaticWrapperAs="desktop"
                slots={{
                  actionBar: null,
                }}
                slotProps={{
                  toolbar: {
                    hidden: false,
                  },
                  textField: {
                    size: "small",
                    fullWidth: true,
                    sx: {
                      zIndex,
                      "& .MuiInputBase-root": {
                        height: "32px",
                        ...(isReadOnly && {
                          backgroundColor: "rgba(0, 0, 0, 0.05)",
                          cursor: "default",
                        }),
                      },
                      "& .MuiInputBase-input": {
                        padding: "0 8px",
                        ...(isReadOnly && {
                          color: "rgba(0, 0, 0, 0.38)",
                          cursor: "default",
                          WebkitTextFillColor: "rgba(0, 0, 0, 0.38)",
                          "&::selection": {
                            backgroundColor: "transparent",
                          },
                        }),
                      },
                      ...(isReadOnly && {
                        "& .MuiIconButton-root": {
                          visibility: "hidden",
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "rgba(0, 0, 0, 0.15)",
                        },
                      }),
                    },
                  },
                  calendarHeader: {
                    sx: {
                      "& .MuiPickersCalendarHeader-root": {
                        paddingLeft: 1,
                        paddingRight: 1,
                      },
                    },
                  },
                }}
                sx={{
                  width: "100%",
                  opacity: isReadOnly ? 0.7 : 1,
                  pointerEvents: isReadOnly ? "none" : "auto",
                  zIndex,
                  "& .MuiPickersLayout-root": {
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    zIndex,
                  },
                  "& .MuiPickersLayout-contentWrapper": {
                    margin: 0,
                    zIndex,
                  },
                  "& .MuiDialogActions-root": {
                    display: "none",
                  },
                  "& .MuiDateCalendar-root": {
                    margin: "0 auto",
                    width: "100%",
                    maxWidth: "320px",
                  },
                  "& .MuiPickersDay-root": {
                    borderRadius: "8px",
                    "&:hover": {
                      backgroundColor: "action.hover",
                    },
                  },
                  "& .MuiPickersDay-today": {
                    border: "1px solid",
                    borderColor: "primary.main",
                  },
                }}
              />
            </LocalizationProvider>
          </div>
        ) : (
          <Typography
            className="text-sm italic"
            style={{
              color: "hsl(var(--foreground))",
            }}
          >
            {formatDateWithPreference(
              regularDate || formatDateToUppercase(dayjs()),
              userDateFormat
            )}
          </Typography>
        )}
      </div>

      {/* Weekday Section */}
      <div
        onClick={() => handleSectionClick(1)}
        className="w-full relative p-4 transition-all duration-200"
        style={{
          borderTop: tabValue === 1 ? "1px solid" : "none",
          borderBottom: "1px solid",
          borderColor:
            tabValue === 1 ? "hsl(var(--primary))" : "hsl(var(--border))",
          zIndex: tabValue === 1 ? 2 : 1,
          marginTop: tabValue === 1 ? "-1px" : 0,
          cursor: isReadOnly ? "default" : "pointer",
          opacity: tabValue === 1 ? 1 : 0.4,
          backgroundColor:
            tabValue === 1 ? "hsl(var(--card))" : "rgba(0, 0, 0, 0.02)",
          pointerEvents: tabValue === 1 ? "auto" : isReadOnly ? "none" : "auto",
          WebkitTapHighlightColor: "transparent",
        }}
        onMouseEnter={(e) => {
          if (!isReadOnly) {
            e.currentTarget.style.borderColor =
              tabValue === 1 ? "hsl(var(--primary))" : "rgba(0, 0, 0, 0.3)";
            e.currentTarget.style.opacity = tabValue === 1 ? "1" : "0.6";
          }
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor =
            tabValue === 1 ? "hsl(var(--primary))" : "hsl(var(--border))";
          e.currentTarget.style.opacity = tabValue === 1 ? "1" : "0.4";
        }}
      >
        <Typography className="mb-1 font-bold text-sm">Weekday</Typography>
        {tabValue === 1 ? (
          <div className="flex flex-col gap-4">
            <FormControl fullWidth>
              <Select
                value={weekdayDate}
                onChange={handleWeekdayDateChange}
                disabled={isReadOnly}
                size="small"
                style={{
                  zIndex: zIndex + 10,
                }}
              >
                {WEEKDAYS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        ) : (
          <Typography
            className="text-sm italic"
            style={{
              color: "hsl(var(--foreground))",
            }}
          >
            {WEEKDAYS.find((wd) => wd.value === weekdayDate)?.label ||
              weekdayDate}
          </Typography>
        )}
      </div>

      {/* Relative Date Section */}
      <div
        onClick={() => handleSectionClick(2)}
        className="w-full relative p-4 transition-all duration-200"
        style={{
          borderTop: tabValue === 2 ? "1px solid" : "none",
          borderBottom: "1px solid",
          borderColor:
            tabValue === 2 ? "hsl(var(--primary))" : "hsl(var(--border))",
          zIndex: tabValue === 2 ? 2 : 1,
          marginTop: tabValue === 2 ? "-1px" : 0,
          cursor: isReadOnly ? "default" : "pointer",
          opacity: tabValue === 2 ? 1 : 0.4,
          backgroundColor:
            tabValue === 2 ? "hsl(var(--card))" : "rgba(0, 0, 0, 0.02)",
          pointerEvents: tabValue === 2 ? "auto" : isReadOnly ? "none" : "auto",
          WebkitTapHighlightColor: "transparent",
        }}
        onMouseEnter={(e) => {
          if (!isReadOnly) {
            e.currentTarget.style.borderColor =
              tabValue === 2 ? "hsl(var(--primary))" : "rgba(0, 0, 0, 0.3)";
            e.currentTarget.style.opacity = tabValue === 2 ? "1" : "0.6";
          }
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor =
            tabValue === 2 ? "hsl(var(--primary))" : "hsl(var(--border))";
          e.currentTarget.style.opacity = tabValue === 2 ? "1" : "0.4";
        }}
      >
        <Typography className="mb-1 font-bold text-sm">Relative</Typography>
        {tabValue === 2 ? (
          <div className="flex flex-col gap-4">
            <FormControl fullWidth>
              <Select
                value={relativeDate}
                onChange={handleRelativeDateChange}
                disabled={isReadOnly}
                size="small"
                style={{
                  zIndex: zIndex + 10,
                }}
              >
                {RELATIVE_DATES.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        ) : (
          <Typography
            className="text-sm italic"
            style={{
              color: "hsl(var(--foreground))",
            }}
          >
            {RELATIVE_DATES.find((rd) => rd.value === relativeDate)?.label ||
              relativeDate}
          </Typography>
        )}
      </div>

      {/* Custom Date Section */}
      <div
        onClick={() => handleSectionClick(3)}
        className="w-full relative p-4 transition-all duration-200"
        style={{
          borderTop: tabValue === 3 ? "1px solid" : "none",
          borderBottom: "1px solid",
          borderColor:
            tabValue === 3 ? "hsl(var(--primary))" : "hsl(var(--border))",
          zIndex: tabValue === 3 ? 2 : 1,
          marginTop: tabValue === 3 ? "-1px" : 0,
          cursor: isReadOnly ? "default" : "pointer",
          opacity: tabValue === 3 ? 1 : 0.4,
          backgroundColor:
            tabValue === 3 ? "hsl(var(--card))" : "rgba(0, 0, 0, 0.02)",
          pointerEvents: tabValue === 3 ? "auto" : isReadOnly ? "none" : "auto",
          WebkitTapHighlightColor: "transparent",
        }}
        onMouseEnter={(e) => {
          if (!isReadOnly) {
            e.currentTarget.style.borderColor =
              tabValue === 3 ? "hsl(var(--primary))" : "rgba(0, 0, 0, 0.3)";
            e.currentTarget.style.opacity = tabValue === 3 ? "1" : "0.6";
          }
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor =
            tabValue === 3 ? "hsl(var(--primary))" : "hsl(var(--border))";
          e.currentTarget.style.opacity = tabValue === 3 ? "1" : "0.4";
        }}
      >
        <Typography className="mb-1 font-bold text-sm">Custom</Typography>
        {tabValue === 3 ? (
          <div className="flex flex-col gap-4 min-h-[200px]">
            <FormControl fullWidth>
              <Typography className="mb-1 text-sm">Year</Typography>
              <Select
                value={customYear}
                onChange={handleCustomYearChange}
                disabled={isReadOnly}
                size="small"
                style={{
                  zIndex: zIndex + 10,
                }}
              >
                {yearOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <Typography className="mb-1 text-sm">Month</Typography>
              <Select
                value={customMonth}
                onChange={handleCustomMonthChange}
                disabled={isReadOnly}
                size="small"
                style={{
                  zIndex: zIndex + 10,
                }}
              >
                {MONTHS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <Typography className="mb-1 text-sm">Day</Typography>
              <Select
                value={customDay}
                onChange={handleCustomDayChange}
                disabled={isReadOnly}
                size="small"
                style={{
                  zIndex: zIndex + 10,
                }}
              >
                {dayOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        ) : (
          <Typography
            className="text-sm italic"
            style={{
              color: "hsl(var(--foreground))",
            }}
          >
            {getCustomDateDisplayText(customYear, customMonth, customDay)}
          </Typography>
        )}
      </div>
    </div>
  );

  return (
    <div
      className="flex flex-col w-full h-full overflow-hidden items-center justify-center"
      style={{ zIndex }}
    >
      <Button
        onClick={handleDialogOpen}
        variant="outlined"
        disabled={isReadOnly}
        className="w-full h-full text-sm justify-center normal-case"
        style={{
          opacity: isReadOnly ? 0.7 : 1,
          cursor: isReadOnly ? "default" : "pointer",
          backgroundColor: isReadOnly ? "rgba(0, 0, 0, 0.05)" : "transparent",
          ...(isInvalidValue && {
            borderColor: "hsl(var(--destructive))",
            color: "hsl(var(--destructive))",
          }),
        }}
        onMouseEnter={(e) => {
          if (isInvalidValue) {
            e.currentTarget.style.borderColor = "hsl(var(--destructive))";
            e.currentTarget.style.backgroundColor =
              "hsl(var(--destructive) / 0.1)";
          }
        }}
        onMouseLeave={(e) => {
          if (isInvalidValue) {
            e.currentTarget.style.borderColor = "hsl(var(--border))";
            e.currentTarget.style.backgroundColor = "transparent";
          }
        }}
      >
        {getDisplayText()}
      </Button>

      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        fullScreen={isSmallScreen}
        PaperProps={{
          style: {
            zIndex: zIndex + 1,
            width: isSmallScreen ? "100%" : "600px",
            maxWidth: isSmallScreen ? "100%" : "600px",
            minWidth: isSmallScreen ? "100%" : "600px",
            height: isSmallScreen ? "100vh" : "90vh",
            paddingTop: isSmallScreen ? `${safeAreaInsets.top}px` : 0,
            backgroundColor: "hsl(var(--card))",
          },
        }}
      >
        <DialogContent
          className="p-0"
          style={{
            backgroundColor: "hsl(var(--card))",
          }}
        >
          {renderContent()}
        </DialogContent>
        <DialogActions className="p-2 gap-1">
          <Button
            onClick={handleCancel}
            variant="outlined"
            className="shadow-none"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            className="shadow-none"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default DateGeneralInput;
